import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import EditorInfoIcon from '@atlaskit/icon/glyph/editor/info';
import EditorWarningIcon from '@atlaskit/icon/glyph/editor/warning';
import { B500, Y500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { useSLAHierarchicalGoalsEnabled } from '../../../../../../../controllers/enable-sla-hierarchical-goals';
import { useGoalCounter } from '../../../../../../../controllers/goal-counter';
import messages from './messages';

type AddGoalButtonViewProps = {
	onAddRequested: () => void;
};

type MessageContainerProps = {
	formatMessage: FormatMessage;
};

const MessageContainerForWarning = ({ formatMessage }: MessageContainerProps) => (
	<>
		<IconContainer>
			<EditorInfoIcon primaryColor={token('color.icon.information', B500)} label="" />
		</IconContainer>
		<TextContainer>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p>{formatMessage(messages.warningLabel)}</p>
		</TextContainer>
	</>
);

const MessageContainerForError = ({ formatMessage }: MessageContainerProps) => (
	<>
		<IconContainer>
			<EditorWarningIcon primaryColor={token('color.icon.danger', Y500)} label="" />
		</IconContainer>
		<TextContainer>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p>{formatMessage(messages.errorLabel)}</p>
		</TextContainer>
	</>
);

const AddGoalButtonView = ({ onAddRequested }: AddGoalButtonViewProps) => {
	const [state] = useGoalCounter();
	const { formatMessage } = useIntl();
	const [{ isSLAHierarchicalGoalsEnabled }] = useSLAHierarchicalGoalsEnabled();
	return isSLAHierarchicalGoalsEnabled ? (
		<>
			<GoalsDisplayMessageContainer>
				<Button
					iconBefore={<EditorAddIcon label="" size="medium" />}
					appearance="link"
					onClick={onAddRequested}
					testId="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.add-goal-button.button"
				>
					{formatMessage(messages.addGoal)}
				</Button>
				{state.yellowFlagForGoalCount && (
					<ActionsContainer>
						<MessageContainerForWarning formatMessage={formatMessage} />
					</ActionsContainer>
				)}
				{state.redFlagForGoalCount && (
					<ActionsContainer>
						<MessageContainerForError formatMessage={formatMessage} />
					</ActionsContainer>
				)}
			</GoalsDisplayMessageContainer>
		</>
	) : (
		<Button
			testId="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.add-goal-button.button"
			appearance="subtle"
			iconAfter={<EditorAddIcon label="" />}
			onClick={onAddRequested}
			shouldFitContainer
			aria-label={formatMessage(messages.addNewGoal)}
		/>
	);
};

export const AddGoalButton = AddGoalButtonView;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalsDisplayMessageContainer = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.div({
	font: token('font.body'),
	fontStretch: 'normal',
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled.div({
	paddingRight: token('space.100', '8px'),
	alignItems: 'center',
	paddingTop: token('space.050', '4px'),
});
